import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { AccentName } from '@/theme';

interface EmptyStateProps {
  title: string;
  description?: string;
  /** Tints the illustration to match the register being filtered, when there is one. */
  accent?: AccentName;
  action?: ReactNode;
}

/**
 * Spec §9.3: empty states get a drawing and a clear call to action, not a
 * shrug. The illustration is inline SVG drawn from theme colours so it works in
 * both modes without shipping two asset files.
 */
export function EmptyState({ title, description, accent, action }: EmptyStateProps) {
  return (
    <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', py: 7, px: 3 }} role="status">
      <EmptyIllustration accent={accent} />

      <Stack spacing={0.75} sx={{ maxWidth: 380 }}>
        <Typography variant="h4" component="p">
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </Stack>

      {action && <Box sx={{ pt: 0.5 }}>{action}</Box>}
    </Stack>
  );
}

/**
 * Three bars — the registers — with the middle one dashed and empty: the shape
 * of the product with something missing from it.
 */
function EmptyIllustration({ accent }: { accent?: AccentName }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 120 84"
      aria-hidden
      sx={{ width: 120, height: 84, color: 'text.disabled' }}
    >
      <rect x="10" y="10" width="100" height="18" rx="9" fill="currentColor" opacity="0.16" />
      <Box
        component="rect"
        x="10"
        y="34"
        width="100"
        height="18"
        rx="9"
        fill="none"
        strokeWidth="1.5"
        strokeDasharray="5 5"
        sx={{
          stroke: (theme) => (accent ? theme.palette[accent].main : theme.palette.text.disabled),
          opacity: accent ? 0.9 : 0.5,
        }}
      />
      <rect x="10" y="58" width="64" height="18" rx="9" fill="currentColor" opacity="0.16" />
    </Box>
  );
}
